import type { HydraContext } from "../hydra.interface";

export enum DeviceTypeEnum {
  MOBILE = 1,
  TABLET = 2,
  BROWSER = 3,
}

export enum DevicePushTokenTypeEnum {
  FCM = 1,
  APN = 2,
  EXPO = 3,
  FCM_WEB = 4,
}

export interface DevicePushToken {
  type: DevicePushTokenTypeEnum
  token: string
}

export interface DeviceMeta {
  name?: string
  brand?: string
  userAgent?: string
  model?: string
  operatingSystemName?: string
  operatingSystemVersion?: string
  rooted?: boolean
  fontScale?: string
  runtimeVersion?: string
  buildNumber?: number
  bundleVersion?: string
  lastAppUpdateAt?: string
  emulator?: boolean
  hermes?: boolean
  channel: "local" | "testing" | "staging" | "production"
  countryCode?: string | null
  countryResolutionStatus?: "resolved" | "unresolved" | null
  countryResolvedAt?: string | null
}

export interface RegisterDevice {
  identifier: string
  type: DeviceTypeEnum
  pushTokens?: DevicePushToken[]
  meta?: DeviceMeta
}

export type DeviceHydraCollectionItemItem = HydraContext & {
  "@type": "Device"
  "relatedUser": string
  "id": number
  "createdAt": string
  "updatedAt"?: string | null
  /**
   * When the device last registered or made an authenticated request in its session. Absent for devices not seen
   * since this is tracked; updatedAt (else createdAt) is then the last sign of it.
   */
  "lastSeenAt"?: string | null
} & Pick<RegisterDevice, "identifier" | "type" | "meta">;

export type DeviceHydraItem = DeviceHydraCollectionItemItem & Pick<RegisterDevice, "meta">;

/** A push token in the device details: only its last characters, never the full token. */
export interface DeviceDetailsPushToken {
  type: DevicePushTokenTypeEnum
  maskedToken: string
}

/**
 * `GET /devices/{id}/details`: the support view of one device, for holders of `device read_any` only. `lastSeenAt` is
 * the latest of the device's own last seen time and the last use of its session; `sessionActive` comes from that
 * session. Both are absent when unknown. `latestAppVersion` is the newest released version for the device's platform
 * (iOS or Android); `runsLatestAppVersion` is absent when the platform, the device's version or the newest version is
 * unknown.
 */
export type DeviceDetailsHydraItem = HydraContext & {
  "@type": "DeviceDetails"
  "id": number
  "device": string
  "relatedUser"?: string | null
  "identifier": string
  "type"?: DeviceTypeEnum | null
  "meta"?: DeviceMeta | null
  "pushTokens": DeviceDetailsPushToken[]
  "lastSeenAt"?: string | null
  "sessionActive"?: boolean | null
  "minimumAppVersion"?: string | null
  "belowMinimumAppVersion"?: boolean | null
  "latestAppVersion"?: string | null
  "runsLatestAppVersion"?: boolean | null
  "createdAt"?: string | null
  "updatedAt"?: string | null
};
